import { observable, configure, action } from 'mobx';
import util from 'preact-util';
import LocalModel from './localModel';
import { isRevenueReason } from '../lib/ammoReasons';
import { trendRange, STOCK_SERIES_DAYS } from '../lib/ammoStats';

configure({ enforceActions: 'always' });

function cleanFilters(filters = {}) {
    const out = {};
    Object.keys(filters).forEach(key => {
        if (filters[key] !== '' && filters[key] !== null && filters[key] !== undefined) {
            out[key] = filters[key];
        }
    });
    return out;
}

class AmmoSaleStore extends LocalModel {
    constructor() {
        super('ammoSale', {
            namePlural: 'ammoSales',
            namePluralReal: 'ammosales',
            sort: '-saleDate',
            limit: 100,
            api: {
                search: {
                    url: '/api/ammosales/',
                    params: {
                        limit: 100,
                        sort: '-saleDate',
                    },
                },
                load: {
                    url: '/api/ammosales/',
                    params: {},
                },
                save: {
                    url: '/api/ammosales/',
                    params: {},
                },
                delete: {
                    url: '/api/ammosales/',
                    params: {},
                },
            },
        });
    }

    @observable ammoSales = [];

    @observable ammoSale = {};

    @observable newAmmoSale = {};

    @observable total = 0;

    @observable stats = {
        byCaliber: [], byReason: [], byMonth: [], byMonthCaliber: [],
    };

    /** byMonthCaliber rows for the fixed 12-month trend window (independent of the period filter). */
    @observable trend = [];

    @observable products = [];

    /** Daily stock level per ammo product for the stock-table micro graphs, keyed by product id. */
    @observable stockSeries = {};

    @observable members = [];

    @observable myPurchases = [];

    @observable rangeOfficers = [];

    @observable isLoading = false;

    /** Body for POST and PATCH. Numbers coerced; payment method only for revenue reasons. */
    buildRegisterBody({
        userId, productId, qty, reason, paymentMethod, comment, saleDate,
    }) {
        const body = {
            userId: parseInt(userId, 10),
            productId: parseInt(productId, 10),
            qty: parseInt(qty, 10),
            reason,
            paymentMethod: isRevenueReason(reason) ? (paymentMethod || '') : '',
            comment: comment || '',
        };
        if (saleDate) {
            body.saleDate = saleDate;
        }
        return body;
    }

    @action
    async loadProducts() {
        const response = await util.fetchApi('/api/ammosales/products', { method: 'GET' }, {});
        if (response?.status === 200) {
            this.updateKeyValue('products', response.data || []);
        }
        return response;
    }

    /** Daily stock level for every ammo product (last `days` days) from /api/ammosales/stock-series. */
    @action
    async loadStockSeries(days = STOCK_SERIES_DAYS) {
        const response = await util.fetchApi('/api/ammosales/stock-series', { method: 'GET' }, { days });
        if (response?.status === 200) {
            const byProduct = {};
            (response.data || []).forEach(row => {
                byProduct[row.productId] = row.points || [];
            });
            this.updateKeyValue('stockSeries', byProduct);
        }
        return response;
    }

    @action
    async searchMembers(search) {
        const term = (search || '').trim();
        if (term.length < 2) {
            this.updateKeyValue('members', []);
            return null;
        }
        const response = await util.fetchApi('/api/ammosales/members', { method: 'GET' }, { search: term });
        if (response?.status === 200) {
            this.updateKeyValue('members', response.data || []);
        }
        return response;
    }

    @action
    async registerSale(input) {
        const body = this.buildRegisterBody(input);
        return util.fetchApi('/api/ammosales/', { method: 'POST' }, body);
    }

    @action
    async updateSale(id, input) {
        const body = this.buildRegisterBody(input);
        return util.fetchApi(`/api/ammosales/${id}`, { method: 'PATCH' }, body);
    }

    @action
    async loadList(filters = {}, { limit = 100, offset = 0 } = {}) {
        this.updateKeyValue('isLoading', true);
        const response = await util.fetchApi('/api/ammosales/', { method: 'GET' }, { ...cleanFilters(filters), limit, offset });
        if (response?.status === 200) {
            this.updateKeyValue('ammoSales', response.data || []);
            this.updateKeyValue('total', response.total || 0);
        }
        this.updateKeyValue('isLoading', false);
        return response;
    }

    @action
    async loadStats(filters = {}) {
        const response = await util.fetchApi('/api/ammosales/stats', { method: 'GET' }, cleanFilters(filters));
        if (response?.status === 200) {
            this.updateKeyValue('stats', response.data || {
                byCaliber: [], byReason: [], byMonth: [], byMonthCaliber: [],
            });
        }
        return response;
    }

    /**
     * Trend rows for the last 12 months. Honours the caliber and reason filters but
     * replaces the period with the trend window so the chart keeps its context when
     * "This month" is selected.
     */
    @action
    async loadTrend(filters = {}) {
        const { startDate, endDate, ...rest } = filters;
        const response = await util.fetchApi('/api/ammosales/stats', { method: 'GET' }, {
            ...cleanFilters(rest),
            ...trendRange(),
        });
        if (response?.status === 200) {
            this.updateKeyValue('trend', response.data?.byMonthCaliber || []);
        }
        return response;
    }

    @action
    async loadMine() {
        const response = await util.fetchApi('/api/ammosales/my', { method: 'GET' }, {});
        if (response?.status === 200) {
            this.updateKeyValue('myPurchases', response.data || []);
        }
        return response;
    }

    /** Club members with the range-officer role (skyteledere), for the Klubbdrift list + SMS. */
    @action
    async loadRangeOfficers() {
        this.updateKeyValue('isLoading', true);
        const response = await util.fetchApi('/api/users/range-officers/', { method: 'GET' }, {});
        if (response?.status === 200) {
            this.updateKeyValue('rangeOfficers', response.data || []);
        }
        this.updateKeyValue('isLoading', false);
        return response;
    }
}

const store = new AmmoSaleStore();
export default store;
